import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { reportsAPI } from '../../services/api';
import {
  Home,
  Users,
  FileText,
  TrendingUp,
  Bell,
  BarChart3,
  UserCheck,
  Settings,
  LogOut,
  UserCircle,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Plus,
  AlertCircle,
  Calendar,
  Shield
} from 'lucide-react';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(true);
  const [reportsOpen, setReportsOpen] = useState(true);
  const [badges, setBadges] = useState({ overdueTasksCount: 0, unreadNotifications: 0 });

  useEffect(() => {
    let cancelled = false;
    const fetchBadges = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const response = await reportsAPI.getBadges();
        if (!cancelled) setBadges(response.data);
      } catch (err) {
        // Badge counts are decorative; stay silent on failure
      }
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const badgeCountFor = (path) => {
    if (path === '/workflow/overdue') return badges.overdueTasksCount;
    if (path === '/notifications') return badges.unreadNotifications;
    return 0;
  };

  const menuItems = [
    {
      text: 'Dashboard',
      icon: Home,
      path: '/dashboard'
    },
    {
      text: 'Clients',
      icon: Users,
      path: '/clients'
    },
    {
      text: 'New Intake',
      icon: Plus,
      path: '/clients/new',
      highlight: true
    },
    {
      text: 'Workflow',
      icon: TrendingUp,
      expandable: true,
      open: workflowOpen,
      setOpen: setWorkflowOpen,
      subItems: [
        {
          text: 'Pipeline View',
          path: '/workflow/pipeline'
        },
        {
          text: 'Overdue Tasks',
          path: '/workflow/overdue',
          badge: true
        },
        {
          text: 'Upcoming Starts',
          path: '/workflow/upcoming'
        },
        {
          text: 'Upcoming Tasks',
          path: '/workflow/tasks'
        }
      ]
    },
    {
      text: 'Reports',
      icon: BarChart3,
      expandable: true,
      open: reportsOpen,
      setOpen: setReportsOpen,
      subItems: [
        {
          text: 'Coverage Report',
          path: '/reports/coverage'
        },
        {
          text: 'Training Compliance',
          path: '/reports/training'
        }
      ]
    },
    {
      text: 'Staff',
      icon: UserCheck,
      path: '/staff'
    },
    {
      text: 'Notifications',
      icon: Bell,
      path: '/notifications',
      badge: true
    },
    {
      text: 'User Management',
      icon: Shield,
      path: '/admin/users',
      adminOnly: true
    }
  ];

  const isActive = (path) =>
    location.pathname === path ||
    (path !== '/' && path !== '/clients/new' && location.pathname.startsWith(path + '/'));

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <div className="flex items-center">
              <img src="/logo.png" alt="COD Health Care" className="h-10 w-10 object-contain" />
              <span className="ml-3 text-lg font-semibold text-gray-900">COD Health Care</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              aria-label="Close menu"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              // Skip admin-only items if user is not an admin
              if (item.adminOnly && !['Admin', 'System Admin'].includes(user?.role)) {
                return null;
              }

              if (item.expandable) {
                return (
                  <div key={item.text}>
                    <button
                      onClick={() => item.setOpen(!item.open)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
                    >
                      <div className="flex items-center">
                        <item.icon className="h-5 w-5 mr-3" />
                        <span>{item.text}</span>
                      </div>
                      {item.open ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    {item.open && (
                      <div className="mt-1 ml-11 space-y-1">
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.text}
                            to={subItem.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`block px-3 py-2 text-sm rounded-lg transition-colors ${
                              isActive(subItem.path)
                                ? 'bg-primary-50 text-primary-700 font-medium'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span>{subItem.text}</span>
                              {subItem.badge && badgeCountFor(subItem.path) > 0 && (
                                <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center text-xs font-semibold text-white bg-danger-500 rounded-full">
                                  {badgeCountFor(subItem.path)}
                                </span>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.text}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    item.highlight
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : isActive(item.path)
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center flex-1">
                    <item.icon className="h-5 w-5 mr-3" />
                    <span>{item.text}</span>
                  </div>
                  {item.badge && badgeCountFor(item.path) > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center text-xs font-semibold text-white bg-danger-500 rounded-full">
                      {badgeCountFor(item.path)}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Settings at bottom */}
          <div className="px-4 py-4 border-t border-gray-200">
            <Link
              to="/settings"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Settings className="h-5 w-5 mr-3" />
              <span>Settings</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Mobile menu button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5 text-gray-500" />
              </button>

              {/* Page title */}
              <div className="flex-1 px-4 lg:px-0">
                <p className="text-lg font-semibold text-gray-900">
                  Client Intake Process Management
                </p>
              </div>

              {/* Right side actions */}
              <div className="flex items-center gap-2">
                {/* Notifications */}
                <button
                  onClick={() => navigate('/notifications')}
                  className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label={`Notifications${badges.unreadNotifications > 0 ? ` (${badges.unreadNotifications} unread)` : ''}`}
                >
                  <Bell className="h-5 w-5 text-gray-500" />
                  {badges.unreadNotifications > 0 && (
                    <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-semibold text-white bg-danger-500 rounded-full">
                      {badges.unreadNotifications > 99 ? '99+' : badges.unreadNotifications}
                    </span>
                  )}
                </button>

                {/* Profile dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    aria-label="Account menu"
                    aria-expanded={profileMenuOpen}
                  >
                    <div className="hidden sm:block text-right">
                      <p className="text-sm font-medium text-gray-700">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-gray-500">{user?.role}</p>
                    </div>
                    <div className="h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user?.firstName?.[0]}{user?.lastName?.[0]}
                      </span>
                    </div>
                  </button>

                  {/* Profile dropdown menu */}
                  {profileMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setProfileMenuOpen(false)}
                      />
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                        <div className="py-1">
                          <Link
                            to="/profile"
                            onClick={() => setProfileMenuOpen(false)}
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <UserCircle className="h-4 w-4 mr-3" />
                            Profile
                          </Link>
                          <Link
                            to="/settings"
                            onClick={() => setProfileMenuOpen(false)}
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Settings className="h-4 w-4 mr-3" />
                            Settings
                          </Link>
                          <hr className="my-1 border-gray-200" />
                          <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <LogOut className="h-4 w-4 mr-3" />
                            Logout
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;